/**
 * API Client Configuration
 *
 * Configures the OpenAPI-generated client with proper settings for
 * cookie-based authentication and CSRF protection.
 *
 * Key configurations:
 * - BASE: API base URL from environment
 * - WITH_CREDENTIALS: Enable cookies to be sent with requests
 * - CREDENTIALS: 'include' to send cookies cross-origin
 *
 * This allows the generated API client to work with the backend's
 * cookie-based session authentication (Requirement 1.1-1.7).
 */

import { OpenAPI } from "../api-client";
import type { ApiRequestOptions } from "../api-client/core/ApiRequestOptions";
import { getCsrfTokenFromCookie, requiresCsrfToken } from "./csrf";

/**
 * Initialize API client configuration.
 *
 * Must be called before making any API requests.
 * Typically called in main.tsx during app initialization.
 */
export function initializeApiClient(): void {
  // Set base URL from environment variable
  OpenAPI.BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";

  // Enable credentials (cookies) to be sent with requests
  // This is required for session cookie authentication
  OpenAPI.WITH_CREDENTIALS = true;
  OpenAPI.CREDENTIALS = "include";

  // Add CSRF token to mutating requests (POST, PUT, PATCH, DELETE)
  // The generated OpenAPI client doesn't use axiosInstance interceptors,
  // so we need to add CSRF token via OpenAPI.HEADERS
  OpenAPI.HEADERS = async (options: ApiRequestOptions): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {};
    
    // Add CSRF token for mutating requests
    if (requiresCsrfToken(options.method)) {
      const csrfToken = getCsrfTokenFromCookie();
      if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken;
        console.log('[ApiClient] Adding CSRF token to request:', options.method, options.url);
      } else {
        console.warn('[ApiClient] No CSRF token found for mutating request:', options.method, options.url);
      }
    }
    
    return headers;
  };

  console.log('[ApiClient] Initialized with BASE:', OpenAPI.BASE);
  console.log('[ApiClient] WITH_CREDENTIALS:', OpenAPI.WITH_CREDENTIALS);
  console.log('[ApiClient] CSRF token handling enabled via HEADERS function');
}

/**
 * Get the configured API base URL.
 *
 * Useful for constructing URLs outside of the generated client.
 *
 * @returns The API base URL
 */
export function getApiBaseUrl(): string {
  return OpenAPI.BASE || import.meta.env.VITE_API_URL || "http://localhost:8000";
}
